import React from 'react';
import PropTypes from 'prop-types';

/**
 * Loading component that displays either a COGSLOG seedling loader SVG or skeleton placeholder UI.
 * Used throughout the application to indicate data is being fetched.
 *
 * @param {Object} props
 * @param {('spinner'|'skeleton')} props.variant - Display style: "spinner" for a
 *   centered COGSLOG seedling loader animation, "skeleton" for gray placeholder bars with shimmer.
 * @param {string} [props.message] - Optional text displayed below the loader.
 *   Only shown for the "spinner" variant.
 * @param {number} [props.rows=4] - Number of skeleton bars to render.
 *   Only used for the "skeleton" variant.
 * @param {('sm'|'md'|'lg')} [props.size='md'] - Loader SVG size: "sm" (24x54),
 *   "md" (48x108), or "lg" (71x160). Dimensions follow the new cogslog-icon-color aspect.
 * @param {('light'|'dark')} [props.theme='light'] - Loader color theme for
 *   light or dark backgrounds.
 * @returns {React.ReactElement} The loading indicator element
 *
 * @example
 * // Seedling loader with message
 * <Loading variant="spinner" message="Loading dashboard data..." />
 *
 * @example
 * // Skeleton with 6 rows
 * <Loading variant="skeleton" rows={6} />
 *
 * @example
 * // Large dark-themed loader
 * <Loading size="lg" theme="dark" />
 */
function Loading({ variant = 'spinner', message, rows = 4, size = 'md', theme = 'light' }) {
  if (variant === 'skeleton') {
    return (
      <div className="loading-container">
        <div className="skeleton-container">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="skeleton-bar"></div>
          ))}
        </div>
      </div>
    );
  }

  const sizeMap = {
    sm: { width: 24, height: 54 },
    md: { width: 48, height: 108 },
    lg: { width: 71, height: 160 },
  };

  const { width, height } = sizeMap[size] || sizeMap.md;

  return (
    <div className="loading-container" role="status" aria-live="polite">
      <img
        src={`/logos/loader/cogslog-loader-${theme}-${size}.svg`}
        width={width}
        height={height}
        alt="Loading..."
        className="cogslog-loader"
      />
      {message && <p className="loading-text">{message}</p>}
    </div>
  );
}

Loading.propTypes = {
  /** Display style: "spinner" for seedling loader, "skeleton" for placeholder bars */
  variant: PropTypes.oneOf(['spinner', 'skeleton']),
  /** Optional text displayed below the loader (spinner variant only) */
  message: PropTypes.string,
  /** Number of skeleton bars to render (skeleton variant only) */
  rows: PropTypes.number,
  /** Loader SVG size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Loader color theme for light or dark backgrounds */
  theme: PropTypes.oneOf(['light', 'dark']),
};

export default Loading;
